import { useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from "react-simple-maps";
import { Plus, Minus, RotateCcw } from "lucide-react";
import { JURISDICTIONS } from "@/data/jurisdictions";

const GEO_URL = "/world-countries-110m.json";

const FOREST = "#2D5016";
const GOLD = "#C9A84C";
const BURNT = "#B85C38";
const UNCOVERED = "#E4DFD0";

// ISO alpha-3 -> ISO numeric (world-atlas country ids are zero-padded numeric strings)
const ISO_N3 = {
  ARE: "784", OMN: "512", SAU: "682", KEN: "404", TZA: "834", UGA: "800",
  RWA: "646", BDI: "108", SSD: "728", COD: "180", SOM: "706", NGA: "566",
  GHA: "288", ZMB: "894", ZAF: "710", BWA: "072", MOZ: "508", IND: "356",
};

// Covered polygons: non-sub jurisdictions that have a 110m polygon. numeric id -> jurisdiction
const NUMERIC_TO_JURISDICTION = {};
JURISDICTIONS.forEach((j) => {
  if (!j.sub && ISO_N3[j.isoA3]) NUMERIC_TO_JURISDICTION[ISO_N3[j.isoA3]] = j;
});

// Ring markers (free zones / sub-registries + Bahrain, which has no 110m polygon)
const bySlug = (slug) => JURISDICTIONS.find((j) => j.slug === slug);
const RING_MARKERS = [
  { jurisdiction: bySlug("difc"), coordinates: [55.28, 25.21] },
  { jurisdiction: bySlug("adgm"), coordinates: [54.39, 24.47] },
  { jurisdiction: bySlug("bahrain"), coordinates: [50.58, 26.22] },
];

const INITIAL = { coordinates: [35, 5], zoom: 1 };

// dimCorridor: when set (a ledger corridor tab other than "All"), covered
// countries outside that corridor fade to low opacity.
export const AtlasMap = ({ selected, onSelect, dimCorridor }) => {
  const [position, setPosition] = useState(INITIAL);
  const [hovered, setHovered] = useState(null);

  const zoomIn = () => setPosition((p) => ({ ...p, zoom: Math.min(p.zoom * 1.5, 6) }));
  const zoomOut = () => setPosition((p) => ({ ...p, zoom: Math.max(p.zoom / 1.5, 1) }));
  const reset = () => setPosition(INITIAL);

  const selectedSlug = selected?.slug;

  return (
    <div data-testid="atlas-map" className="relative w-full h-full bg-[#F6F1E4]">
      {/* Zoom controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-1.5">
        {[
          { key: "in", label: "Zoom in", Icon: Plus, onClick: zoomIn },
          { key: "out", label: "Zoom out", Icon: Minus, onClick: zoomOut },
          { key: "reset", label: "Reset view", Icon: RotateCcw, onClick: reset },
        ].map(({ key, label, Icon, onClick }) => (
          <button
            key={key}
            type="button"
            data-testid={`atlas-zoom-${key}`}
            onClick={onClick}
            aria-label={label}
            title={label}
            className="w-9 h-9 inline-flex items-center justify-center bg-white/90 border border-charcoal/15 text-charcoal/70 hover:text-forest hover:border-forest transition-colors duration-200"
          >
            <Icon className="w-4 h-4" strokeWidth={1.8} />
          </button>
        ))}
      </div>

      {/* Legend */}
      <div data-testid="atlas-legend" className="absolute bottom-4 left-4 z-10 bg-white/90 border border-charcoal/12 px-3.5 py-3 flex flex-col gap-2">
        {[
          { c: FOREST, l: "Covered", ring: false },
          { c: BURNT, l: "Selected", ring: false },
          { c: GOLD, l: "Free zone", ring: true },
        ].map(({ c, l, ring }) => (
          <span key={l} className="flex items-center gap-2 font-sans text-[10px] tracking-[0.12em] uppercase text-charcoal/70">
            {ring ? (
              <span className="w-3 h-3 rounded-full border-2" style={{ borderColor: c }} />
            ) : (
              <span className="w-3 h-3" style={{ background: c }} />
            )}
            {l}
          </span>
        ))}
      </div>

      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 330, center: [35, 5] }}
        width={800}
        height={620}
        style={{ width: "100%", height: "100%" }}
      >
        {/* Ocean */}
        <rect x={0} y={0} width={800} height={620} fill="#EFE8D6" />
        <ZoomableGroup
          center={position.coordinates}
          zoom={position.zoom}
          minZoom={1}
          maxZoom={6}
          onMoveEnd={(pos) => setPosition(pos)}
          filterZoomEvent={(evt) => evt.type !== "wheel"}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const jur = NUMERIC_TO_JURISDICTION[geo.id];
                const isCovered = Boolean(jur);
                const isSelected = isCovered && jur.slug === selectedSlug;
                const isHovered = isCovered && hovered === geo.id;
                const isDimmed = isCovered && dimCorridor && jur.corridor !== dimCorridor;
                const fill = isSelected ? BURNT : isHovered ? GOLD : isCovered ? FOREST : UNCOVERED;
                const handle = () => isCovered && onSelect(jur);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    opacity={isDimmed ? 0.25 : 1}
                    stroke="#F6F1E4"
                    strokeWidth={0.4}
                    onClick={handle}
                    onMouseEnter={() => isCovered && setHovered(geo.id)}
                    onMouseLeave={() => setHovered(null)}
                    onKeyDown={(e) => {
                      if (isCovered && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        handle();
                      }
                    }}
                    tabIndex={isCovered ? 0 : -1}
                    role={isCovered ? "button" : undefined}
                    aria-label={isCovered ? `${jur.name} — ${jur.corridor}` : undefined}
                    style={{ outline: "none", cursor: isCovered ? "pointer" : "default", transition: "opacity 0.3s ease" }}
                  />
                );
              })
            }
          </Geographies>

          {/* Ring markers (free zones + Bahrain) */}
          {RING_MARKERS.map(({ jurisdiction, coordinates }) => {
            if (!jurisdiction) return null;
            const isSelected = jurisdiction.slug === selectedSlug;
            const isDimmed = dimCorridor && jurisdiction.corridor !== dimCorridor;
            return (
              <Marker
                key={jurisdiction.slug}
                coordinates={coordinates}
                data-testid={`atlas-marker-${jurisdiction.slug}`}
                opacity={isDimmed ? 0.25 : 1}
                onClick={() => onSelect(jurisdiction)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(jurisdiction);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`${jurisdiction.name} — ${jurisdiction.corridor}`}
                style={{ cursor: "pointer", outline: "none", transition: "opacity 0.3s ease" }}
              >
                <circle
                  r={4.5 / position.zoom}
                  fill="transparent"
                  stroke={isSelected ? BURNT : GOLD}
                  strokeWidth={2 / position.zoom}
                />
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
};
